import { NavigationGuardNext, Route } from 'vue-router'
import { RouteNames } from '@/router/routes'
import store from '@/store'
import { Getters } from '@/store/getters'
import ability from '@/plugins/ability'

/**
 * Helper function to check if organisation has a specific module enabled
 */
function hasOrgModuleAccess(moduleName: string): boolean {
  try {
    const user = store.getters[Getters.AUTH_USER]
    if (!user?.organisation_settings) return false
    return user.organisation_settings[moduleName] || false
  } catch (error) {
    return false
  }
}

/**
 * Route Guard Configuration
 * Define the abilities/permissions required for each route
 */
export interface RouteGuardConfig {
  // The route name this guard applies to
  routeName: string
  // Array of abilities required (user must have at least one)
  anyAbilities?: Array<{ action: string; subject: string | null }>
  // Array of abilities required (user must have all)
  allAbilities?: Array<{ action: string; subject: string | null }>
  // Custom guard function for complex logic
  // Return true to allow, false to deny. Guard function is responsible for calling next()
  customGuard?: (to: Route, from: Route, next: NavigationGuardNext) => Promise<boolean | void>
}

/**
 * Define your route guards here
 * Each route can have:
 * - anyAbilities: User needs at least ONE of these abilities
 * - allAbilities: User needs ALL of these abilities
 * - customGuard: Custom function for complex logic
 */
export const routeGuards: RouteGuardConfig[] = [
  {
    routeName: RouteNames.ADMIN_PLAYERS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_TEAMS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_STAFF,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_LOCATIONS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_DEVICES,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_MEMBERSHIP_PLANS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PARENTS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_POLAR_TEAM,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_GPEXE,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_FIELDWIZ,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_CATAPULT,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_VALD,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_KINEXON,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PLAYER_FORM,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PLAYER_EDIT,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_BILLING,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },

  {
    routeName: RouteNames.PERFORMANCE_EVENT,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_EVENT_PLAYER,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_ADVANCED,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_PERIOD_SUMMARY,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.DIAGNOSTICS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.DIAGNOSTIC_PROTOCOLS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.DIAGNOSTIC_COMPARISON,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.MEDICAL,
    anyAbilities: [
      { action: 'view-medical', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_medical_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.TRAINING_PROTOCOLS,
    anyAbilities: [
      { action: 'view-training-protocols', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_EXERCISES,
    anyAbilities: [
      { action: 'view-training-protocols', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.DRILL_BUILDER,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_drill_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_PROGRAMS,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_programs_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_PROGRAM_INSTANCES,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_programs_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.EVENTS
  },
  {
    routeName: RouteNames.EVENT_STATS,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_event_stats')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_MEDIA,
    anyAbilities: [
      { action: 'view-player-media', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_NOTES,
    anyAbilities: [
      { action: 'view-player-notes', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_ATTENDANCE,
    anyAbilities: [
      { action: 'view-event-attendance', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_DIAGNOSTICS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_MEDICAL,
    anyAbilities: [
      { action: 'view-medical', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_medical_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.MEMBERSHIP_FEES,
    anyAbilities: [
      { action: 'view-membership', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_membership_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.WELLNESS,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_itl_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.DAILY_ACTIVITY,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_daily_activity_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.LOAD,
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_itl_module') && !hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.REPORTS
  },
  {
    routeName: RouteNames.DASHBOARD_PLAYERS
  }
]

/**
 * Check if user has required abilities for a route
 */
export function checkRouteAbilities(guard: RouteGuardConfig): boolean {
  try {
    if (guard.anyAbilities && guard.anyAbilities.length > 0) {
      const hasAnyAbility = guard.anyAbilities.some(({ action, subject }) => {
        try {
          return ability.can(action, subject)
        } catch (error) {
          return false
        }
      })
      if (!hasAnyAbility) return false
    }

    if (guard.allAbilities && guard.allAbilities.length > 0) {
      const hasAllAbilities = guard.allAbilities.every(({ action, subject }) => {
        try {
          return ability.can(action, subject)
        } catch (error) {
          return false
        }
      })
      if (!hasAllAbilities) return false
    }

    return true
  } catch (error) {
    return false
  }
}

/**
 * Get the guard configuration for a route
 */
export function getRouteGuard(routeName: string | null | undefined): RouteGuardConfig | undefined {
  if (!routeName) return undefined
  return routeGuards.find(guard => guard.routeName === routeName)
}

/**
 * Main navigation guard function
 * Call this in your router.beforeEach
 */
export async function navigationGuard(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
  try {
    const isAuthenticated = store.getters[Getters.IS_AUTHENTICATED]

    const guard = getRouteGuard(to.name)

    if (!guard) {
      next()
      return
    }

    if (!isAuthenticated) {
      next({ name: RouteNames.LOGIN })
      return
    }

    const hasAbilities = checkRouteAbilities(guard)
    if (!hasAbilities) {
      next({ name: RouteNames.OVERVIEW })
      return
    }

    if (guard.customGuard) {
      try {
        const result = await guard.customGuard(to, from, next)
        if (result === false) {
          next({ name: RouteNames.OVERVIEW })
        }
      } catch (error) {
        next({ name: RouteNames.OVERVIEW })
      }
      return
    }

    next()
  } catch (error) {
    next({ name: RouteNames.OVERVIEW })
  }
}
